// Google Identity Services (GIS), loaded on demand.
//
// Only the "Sign In With Google" ID-token surface is typed, and only the parts
// the sign-up button uses. The script is third-party, so it is fetched when the
// feature is ON and the sign-up screen is shown, never at app start.

export interface GisCredentialResponse {
  /** A signed Google ID token (JWT). */
  credential?: string;
}

interface GisIdApi {
  initialize: (config: {
    client_id: string;
    callback: (response: GisCredentialResponse) => void;
    nonce: string;
    auto_select?: boolean;
    use_fedcm_for_button?: boolean;
  }) => void;
  renderButton: (
    parent: HTMLElement,
    options: {
      type?: 'standard' | 'icon';
      theme?: 'outline' | 'filled_blue' | 'filled_black';
      size?: 'large' | 'medium' | 'small';
      text?: 'signup_with' | 'signin_with' | 'continue_with';
      shape?: 'rectangular' | 'pill';
      width?: number;
      logo_alignment?: 'left' | 'center';
    },
  ) => void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GisIdApi } };
  }
}

const GIS_SRC = 'https://accounts.google.com/gsi/client';

let loading: Promise<GisIdApi> | null = null;

/** Load the GIS script once and resolve with its ID API. */
export function loadGoogleIdentity(): Promise<GisIdApi> {
  const ready = window.google?.accounts?.id;
  if (ready) return Promise.resolve(ready);
  if (loading) return loading;
  loading = new Promise<GisIdApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const api = window.google?.accounts?.id;
      if (api) resolve(api);
      else reject(new Error('Google Identity Services did not initialise'));
    };
    script.onerror = () => {
      // Allow a later retry (blocked by an extension, offline, CSP).
      loading = null;
      reject(new Error('Google Identity Services failed to load'));
    };
    document.head.appendChild(script);
  });
  return loading;
}
